import { motion } from "framer-motion";
import { FileText, Ruler, Layers, Settings } from "lucide-react";
import { IMG } from "@/lib/images";

const STATS = [
  { icon: Ruler,    label: "Dimensions", value: "360 × 200 × 130 mm" },
  { icon: Layers,   label: "Capacity",   value: "8 – 10 L" },
  { icon: Settings, label: "Hardware",   value: "Fidlock V-Buckle · YKK AquaGuard" },
  { icon: FileText, label: "Tech Pack",  value: "Version 1.0 · May 2026" },
];

export const TechPack = () => {
  return (
    <section id="tech-pack" data-testid="tech-pack-section" className="relative py-28 md:py-36 bg-[#0A0A0C] border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="section-mark" />
              <span className="overline">Engineering · Tech Pack V1.0</span>
            </div>
            <h2 className="h-section text-white text-4xl sm:text-5xl lg:text-6xl">
              Every detail, <br />
              <span className="text-[#F47A20]">documented.</span>
            </h2>
          </div>
          <p className="text-white/55 max-w-sm">
            It started as notebook sketches in the garage. Nine engineering pages later, it&rsquo;s the document the factory builds from.
          </p>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              className="p-4 bg-[#141417] border border-white/5 hover:border-[#F47A20]/30 transition-colors rounded-sm"
              data-testid={`tech-stat-${i}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 grid place-items-center bg-[#1A1A1D] border border-white/5 rounded-sm">
                  <s.icon size={14} className="text-[#F47A20]" strokeWidth={1.6} />
                </div>
                <span className="serial text-[10px] text-white/40">{s.label}</span>
              </div>
              <div className="text-white text-sm md:text-base font-semibold leading-snug">{s.value}</div>
            </motion.div>
          ))}
        </div>

        {/* From the notebook — original sketches, not polished renders */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative"
          data-testid="tech-pack-image"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
            {[
              { img: IMG.GREECE_SKETCH, pos: "50% 35%", cap: "The original notebook" },
              { img: IMG.SKETCH_BOARD, pos: "50% 50%", cap: "First OUTLAW drafts" },
              { img: IMG.SKETCH_TECHPACK_PAGE, pos: "50% 50%", cap: "Sketch to spec sheet" },
            ].map((t, i) => (
              <div key={t.cap} className="group relative aspect-[4/3] md:aspect-[4/5] overflow-hidden rounded-sm border border-white/10 bg-[#141417]" data-testid={`tech-sketch-${i}`}>
                <img
                  src={t.img}
                  alt={t.cap}
                  loading="lazy"
                  decoding="async"
                  style={{ objectPosition: t.pos }}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C]/85 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div className="serial text-[10px] text-[#F47A20]">SKETCH 0{i + 1}</div>
                  <div className="text-white text-sm font-semibold mt-1">{t.cap}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="absolute top-4 left-4 tactical-badge text-[9px]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#F47A20] animate-pulse" />
            From the garage notebook · No renders
          </div>
        </motion.div>

        <p className="mt-6 serial text-[10px] text-white/35 text-center">
          ORIGINAL SKETCHES · NINE-PAGE TECH PACK · VIP MEMBERS RECEIVE FULL TECH PACK ON LAUNCH
        </p>
      </div>
    </section>
  );
};
